import {
	game
} from "../Game";
import {
	Settings
} from "../Settings";
import * as SaveManager from "./SaveManager";
import {MobileControlView} from '../ui/MobileControlView.js';
import {orientationFor, readMobileLayouts} from '../input/mobileLayout.js';
import {TouchDpad} from '../input/TouchDpad.js';
import {Key} from '../../libs/Key.js';
import {input} from '../input/InputManager.js';
import {mobileLevelControls, mobileDirectionKeys, MobileKeyHolds, levelKeyId, placeLevelKey} from '../input/mobileLevelControls.js';
import {wireTouchKey} from '../input/TouchKeyButton.js';
import {MobileLevelMenu, mobileKeyLabel} from '../ui/MobileLevelMenu.js';
import {isMobile, isIpad} from './device.js';

let view;
let dpadInput;
let layouts = readMobileLayouts();
const heldTouches = new MobileKeyHolds((down, key) => fireKeyboardEvent(down, key));
const heldLevelTouches = new MobileKeyHolds((down, key) => Key.setKey(key, down, 'level'));
let levelMenu;
let pinnedResets = [];
let layoutHolder;
let arrowLeft;
let arrowRight;
let accelDown;
let accelUp;
let flip;
let pauseButton;
let actionButton;
let ejectButton;
let jumpButton;
let grabButton;

let dpadHolder;
let dpadLeft;
let dpadRight;
let dpadUp;
let dpadDown;

export const init = () => {

	view = new MobileControlView(document.body, Settings.touchButtonSize);
	layoutHolder = view.root;
	layoutHolder.className = 'mobile-game-controls';
	layoutHolder.style.zIndex = '999';
	layoutHolder.style.opacity = Settings.touchControlsAlpha;
	({left: arrowLeft, right: arrowRight, flip, brake: accelDown, accelerate: accelUp,
		pause: pauseButton, eject: ejectButton, jump: jumpButton, grab: grabButton, action: actionButton, dpad: dpadHolder} = view.controls);
	({up: dpadUp, down: dpadDown, left: dpadLeft, right: dpadRight} = view.directions);
	for (const button of Object.values(view.controls).filter(button => button !== dpadHolder && button !== view.controls.levelControls)) {
		button.style.touchAction = 'none';
		button.ontouchstart = handleButton;
		button.ontouchend = handleButton;
		button.ontouchcancel = handleButton;
	}
	for (const button of Object.values(view.directions)) button.style.pointerEvents = 'none';
	view.dpadTouchArea.style.pointerEvents = 'all';
	dpadInput = new TouchDpad(view.dpadTouchArea, setDpadDirections,
		() => Boolean(game.character) && ['character', 'runner'].includes(view.mode) && layoutHolder.style.display !== 'none');
	levelMenu = new MobileLevelMenu({toggle: view.controls.levelControls, pin: pinLevelKey, wire: wireLevelKey, release: () => levelMenu?.resetTouches()});
	window.addEventListener('blur', releaseTouches);
	document.addEventListener('visibilitychange', () => { if (document.hidden) releaseTouches(); });


	resize();
	hide();


	if (isIos() && !isIOSStandaloneMode()) {
		const userData = SaveManager.getLocalUserdata();
		if (userData.applePWAModals < 3) {
			showApplePWAInstall();
			userData.applePWAModals++;
			SaveManager.updateLocalUserData(userData);
		}
	}

	removeServiceWorker();
}

export const resize = () => {
	if (!layoutHolder) return;
	releaseTouches();
	const orientation = orientationFor(window.innerWidth, window.innerHeight);
	view.resize(window.innerWidth, window.innerHeight, orientation, layouts[orientation]);
	view.applyLevelLayout(mobileLevelControls.layouts[orientation]);
	levelMenu?.render(orientation);
	levelMenu?.position(view.viewport, view.geometry.levelControls);
}
export const reloadLayout = () => { layouts = readMobileLayouts(); resize(); };
export const showActionButton = () => {
	if (!layoutHolder) return;
	actionButton.style.display = 'block'
}
export const hideActionButton = () => {
	if (!layoutHolder) return;
	releaseButton(actionButton);
	actionButton.style.display = 'none'
}
export const setJumpButton = enabled => {
	if (!layoutHolder) return;
	if (!enabled) releaseButton(jumpButton);
	view.setJumpEnabled(enabled);
}
export const showCharacterControls = () => {
	if (!layoutHolder) return;
	[arrowLeft, arrowRight, accelUp, accelDown, ejectButton, jumpButton].forEach(releaseButton);
	view.setMode('character');
}
export const showVehicleControls = () => {
	if (!layoutHolder) return;
	dpadInput?.reset();
	[dpadUp, dpadDown, dpadLeft, dpadRight, grabButton].forEach(releaseButton);
	view.setMode('vehicle');
}
// A vehicle that is a character itself, the Freerunner, is steered with the directional pad.
export const showRunnerControls = () => {
	if (!layoutHolder) return;
	[arrowLeft, arrowRight, accelUp, accelDown, flip].forEach(releaseButton);
	view.setMode('runner');
}

export const hide = () => {
	releaseTouches();
	if (layoutHolder) layoutHolder.style.display = 'none';
	levelMenu?.show(false);
}
export const show = () => {
	// Menus can change the viewport while the gameplay controls are hidden.
	resize();
	if (layoutHolder) layoutHolder.style.display = 'block';
	levelMenu?.show(true);
}
export const configureLevel = (data, triggers) => {
	if (!view) return;
	releaseTouches();
	mobileLevelControls.configure(data, triggers);
	view.setLevelKeys(mobileLevelControls.keys, mobileKeyLabel);
	pinnedResets = Object.values(view.levelButtons).map(button => wireLevelKey(button, Number(button.dataset.levelKey)));
	resize();
};
export const reloadLevelLayout = () => {
	if (!view) return;
	resize();
	levelMenu?.show(layoutHolder.style.display !== 'none');
};
const wireLevelKey = (button, code) => wireTouchKey(button, {
	enabled: () => !!game.run && !game.pause && !game.levelWon && !input.blocked && layoutHolder.style.display !== 'none',
	press: owner => { heldLevelTouches.press(owner, [code]); owner.style.filter = 'brightness(0.5)'; },
	release: owner => { heldLevelTouches.release(owner); owner.style.filter = 'unset'; },
});
const pinLevelKey = (code, pinned) => {
	const orientation = orientationFor(window.innerWidth, window.innerHeight);
	const layout = mobileLevelControls.layouts[orientation];
	if (pinned) layout[levelKeyId(code)] = placeLevelKey(view.viewport, view.levelKeyObstacles());
	else delete layout[levelKeyId(code)];
	mobileLevelControls.save(mobileLevelControls.layouts);
	reloadLevelLayout();
};
export const reset = ()=>{
	hideActionButton();
	showVehicleControls();
}

export {isMobile};

export const isIos = () => {
	const userAgent = window.navigator.userAgent.toLowerCase();
	return (/iphone|ipad|ipod/.test(userAgent) || isIpad());
}
const isIOSStandaloneMode = () => ('standalone' in window.navigator) && (window.navigator.standalone);



const handleButton = event => {
	const {
		currentTarget,
		type
	} = event;

	const buttonDown = type === 'touchstart';
	event.preventDefault();
	if (!buttonDown) {
		if (type === 'touchcancel' || !event.targetTouches?.length) releaseButton(currentTarget);
		return;
	}
	pressButton(currentTarget);
}

const pressButton = currentTarget => {
	if (heldTouches.has(currentTarget) || !game.character) return;
	const charFlipped = game.character.flipped && !game.vehicle?.verticalControls;
	const press = key => {
		heldTouches.press(currentTarget, mobileDirectionKeys(key, mobileLevelControls.required));
		currentTarget.style.filter = 'brightness(0.5)';
	};

	switch (currentTarget) {
		case arrowLeft:
			press(65);
			break
		case arrowRight:
			press(68);
			break
		case accelUp:
			press(charFlipped ? 83 : 87);
			break
		case accelDown:
			press(charFlipped ? 87 : 83);
			break
		case flip:
			press(32);
			break
		case pauseButton:
			press(80);
			break
		case dpadUp:
			// On foot, directions match the keyboard regardless of facing.
			press(87);
			break
		case dpadDown:
			press(83);
			break
		case dpadLeft:
			press(65);
			break
		case dpadRight:
			press(68);
			break
		case ejectButton:
			press(90);
			break
		case grabButton:
			press(70);
			break
		case actionButton:
			press(69);
			break
		case jumpButton:
			press(88);
			break
	}

}


const releaseButton = button => {
    if (!heldTouches.has(button)) return;
    heldTouches.release(button);
    button.style.filter = 'unset';
};
const setDpadDirections = directions => {
    const active = new Set(directions.map(direction => view.directions[direction]));
    for (const button of Object.values(view.directions)) if (!active.has(button)) releaseButton(button);
    for (const button of active) pressButton(button);
};
export const releaseTouches = () => {
    dpadInput?.reset();
    [...heldTouches.owners.keys()].forEach(releaseButton);
    pinnedResets.forEach(reset => reset());
    levelMenu?.resetTouches();
    heldLevelTouches.reset();
};


export const fireKeyboardEvent = (down, key) => {
	const keyEvent = down ? 'keydown' : 'keyup'
	document.body.dispatchEvent(new KeyboardEvent(keyEvent, {
		keyCode: key,
		charCode: key,
	}));
}


export const openFullscreen = userTriggered => {
	if ((isMobile() && window.location.hostname !== 'localhost') || userTriggered) {
		try{
			const fullscreenElement = document.body;
			if (fullscreenElement.requestFullscreen) {
				fullscreenElement.requestFullscreen();
			} else if (fullscreenElement.mozRequestFullScreen) {
				/* Firefox */
				fullscreenElement.mozRequestFullScreen();
			} else if (fullscreenElement.webkitRequestFullscreen) {
				/* Chrome, Safari and Opera */
				fullscreenElement.webkitRequestFullscreen();
			} else if (fullscreenElement.msRequestFullscreen) {
				/* IE/Edge */
				fullscreenElement.msRequestFullscreen();
			}
		}catch(err){
			// err
		}
	}
}
export const exitFullscreen = ()=> {
	/* Close fullscreen */
	try{
		if (document.exitFullscreen) {
		document.exitFullscreen();
		} else if (document.webkitExitFullscreen) { /* Safari */
		document.webkitExitFullscreen();
		} else if (document.msExitFullscreen) { /* IE11 */
		document.msExitFullscreen();
		}
	}catch(err){
		// err
	}
}
export const toggleFullscreen = userTriggered =>{
	if(document.fullscreenElement){
		exitFullscreen();
	}else{
		openFullscreen(userTriggered);
	}
}

const showApplePWAInstall = () => {
	const prompt = document.createElement('div');
	prompt.classList.add('ios-pwa-container');

	const pwa = document.createElement('div');
	pwa.classList.add('ios-pwa');
	prompt.appendChild(pwa);

	document.body.appendChild(prompt);

	const iconContainer = document.createElement('div');
	iconContainer.classList.add('ios-pwa-icon-container')
	pwa.appendChild(iconContainer);

	const thumb = document.createElement('div');
	iconContainer.appendChild(thumb);
	thumb.classList.add('ios-pwa-icon-thumb');
	thumb.style.backgroundImage = 'url(./android-chrome-60x60.png)'

	const cross = document.createElement('div');
	cross.classList.add('ios-pwa-cross');
	cross.innerText = '✕';
	pwa.appendChild(cross);

	cross.onclick = () => {
		if (prompt && prompt.parentNode) {
			prompt.parentNode.removeChild(prompt);
		}
	}

	const content = document.createElement('div');
	content.classList.add('ios-pwa-content');
	pwa.appendChild(content);

	const text = document.createElement('div');
	text.classList.add('ios-pwa-text');
	text.innerHTML = "<span>Install <strong>JollyWorld</strong> on your home screen for <strong>fullscreen</strong> gameplay and quick and easy access when you&rsquo;re on the go.</span>"
	content.appendChild(text);

	const guide = document.createElement('p');
	guide.classList.add('ios-pwa-guide');
	content.appendChild(guide);

	guide.innerHTML = `
	Just tap <svg class="ios-pwa-guide-icon" viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><title>Share</title><path fill="#007AFF" d="M48.883,22.992L61.146,10.677L61.146,78.282C61.146,80.005 62.285,81.149 64,81.149C65.715,81.149 66.854,80.005 66.854,78.282L66.854,10.677L79.117,22.992C79.693,23.57 80.256,23.853 81.114,23.853C81.971,23.853 82.534,23.57 83.11,22.992C84.25,21.848 84.25,20.125 83.11,18.981L65.997,1.794C65.715,1.511 65.421,1.215 65.139,1.215C64.563,0.932 63.718,0.932 62.861,1.215C62.579,1.498 62.285,1.498 62.003,1.794L44.89,18.981C43.75,20.125 43.75,21.848 44.89,22.992C46.029,24.149 47.744,24.149 48.883,22.992ZM103.936,35.32L81.114,35.32L81.114,41.053L103.936,41.053L103.936,121.27L24.064,121.27L24.064,41.053L46.886,41.053L46.886,35.32L24.064,35.32C20.928,35.32 18.355,37.904 18.355,41.053L18.355,121.27C18.355,124.419 20.928,127.003 24.064,127.003L103.936,127.003C107.072,127.003 109.645,124.419 109.645,121.27L109.645,41.053C109.645,37.891 107.072,35.32 103.936,35.32Z"></path></svg> then “Add to Home Screen”
	`
	prompt.style.visibility = 'hidden';
	pwa.style.opacity = 0;

	if(isIpad()){
		pwa.classList.add('ipad');
		prompt.classList.add('ipad');
	}

	setTimeout(() => {
		pwa.style.opacity = 1.0;
		prompt.style.visibility = 'visible';
	}, 300);
}


const removeServiceWorker = () => {
	if (!('serviceWorker' in navigator)) return;
	const scriptURL = new URL('./sw.js', window.location.href).href;
	navigator.serviceWorker.getRegistrations().then(registrations => {
		for (const registration of registrations) {
			const worker = registration.active || registration.waiting || registration.installing;
			if (worker?.scriptURL === scriptURL) registration.unregister();
		}
	}).catch(() => {});
	window.caches?.keys().then(keys => Promise.all(
		keys.filter(key => key.startsWith('JollyWorld-')).map(key => window.caches.delete(key))
	)).catch(() => {});
}
